import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import type { EventWithCircuit, EventSource } from '../api/eventsService';

// Ensure dayjs plugins are loaded
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Helper function to determine the correct data source for an event
 * based on its type (demo, legacy, or regular)
 * 
 * @param event - The event to determine the source for
 * @returns The appropriate EventSource
 */
export function getEventSource(event: EventWithCircuit): EventSource {
  if (event.demo) return 'demo';
  if (event.legacy) return 'legacy';
  return 'prod';
}

export type EventDateStatus = 'upcoming' | 'active' | 'ended';

/**
 * Determine whether an event is upcoming, currently active, or ended
 * based on today's date in the circuit's timezone.
 */
export function getEventDateStatus(
  startDate: string,
  endDate: string,
  circuitTimeZone?: string
): EventDateStatus {
  const todayInCircuitTz = dayjs().tz(circuitTimeZone || 'UTC').format('YYYY-MM-DD');

  if (todayInCircuitTz < startDate) return 'upcoming';
  if (todayInCircuitTz > endDate) return 'ended';
  return 'active';
}

/**
 * Check if an event is currently active based on the circuit's timezone.
 * An event is considered active if today's date (in the circuit's timezone)
 * falls within the event's start_date and end_date.
 * 
 * This provides a consistent calculation across the entire application,
 * matching the backend's logic which also uses the circuit's timezone.
 * 
 * @param event - The event to check
 * @returns true if the event is currently active, false otherwise
 */
export function isEventActive(event: EventWithCircuit | null | undefined): boolean {
  if (!event) return false;

  return getEventDateStatus(
    event.start_date,
    event.end_date,
    event.circuit?.time_zone
  ) === 'active';
}

/**
 * Helper to get a human-readable label for an event source
 * 
 * @param source - The event source
 * @returns A human-readable label
 */
export function getEventSourceLabel(source: EventSource): string {
  const labels: Record<EventSource, string> = {
    'prod': 'Real Event',
    'legacy': 'Legacy Event',
    'demo': 'Demo Event',
  };
  return labels[source];
}

